import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ICountry } from '../../../../../core/models/country.interface';
import { PageParams } from '../../../../../core/models/page-params.model';
import { IPaginatorProperties } from '../../../../../core/models/paginator.interface';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import {
    ICarrierItem,
    IPriceListFilter,
    IService,
    IShipmentPriceAction,
    ISubCarrierPrice,
    ISubCarrierPriceError,
    ISubService,
    IVendor,
} from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';

@Component({
    selector: 'eci-last-mile',
    templateUrl: './last-mile.component.html',
    styleUrls: ['./last-mile.component.scss']
})
export class LastMileComponent implements OnInit {
    public form: FormGroup;

    public priceList: ISubCarrierPriceError[] = [];
    public showLoader = false;
    public currentSort: Sort = {active: 'createdAt', direction: 'desc'};
    public pageParams: PageParams = new PageParams();

    @Input() public vendors: IPaginatorProperties<IVendor> = {items: [], meta: undefined};
    @Input() public services: IPaginatorProperties<IService> = {items: [], meta: undefined};
    @Input() public carriers: IPaginatorProperties<ICarrierItem> = {items: [], meta: undefined};
    @Input() public subServices: IPaginatorProperties<ISubService> = {items: [], meta: undefined};
    @Input() public files: IPaginatorProperties<{ id: number; fileName: string }> = {items: [], meta: undefined};
    @Input() public countriesList: ICountry[];

    private searchValue: string;
    private filters: IPriceListFilter;

    public dataSource: MatTableDataSource<ISubCarrierPrice>;
    public dynamicColumns: string[];
    public displayedColumns: string[] = [
        'fileName',
        'vendor',
        'service',
        'subService',
        'carrierProvider',
        'destination',
        'costNetRates',
        'margin',
        'sellPrice',
        'unit',
        'subCarrier',
        'currency',
        'weight',
        'errorReason',
        'action',
    ];
    public displayedFields = ['vendors', 'services', 'subServices', 'carriers'];

    public constructor(private userSvc: UserService,
                       private notify: NotificationService,
                       // tslint:disable-next-line:no-parameter-properties
                       private shipmentPriceSvc: ShipmentPriceService) {
    }

    public ngOnInit(): void {
        this.getList();
    }

    private getList(page: number = this.pageParams.pageIndex + 1 || 1,
                    limit: number = this.pageParams.pageSize,
                    q: string = this.searchValue || '',
                    orderField: string = this.currentSort?.active || '',
                    orderDir: string = this.currentSort?.direction.toUpperCase() || ''): void {
        this.showLoader = true;
        this.shipmentPriceSvc.get<IPaginatorProperties<ISubCarrierPriceError>>(
            'sub-carrier-prices-error', page, limit, q, this.filters, orderField, orderDir)
            .subscribe((data) => {
                if (!data) {
                    return;
                }
                this.priceList = data.items;

                this.dataSource = new MatTableDataSource<ISubCarrierPriceError>(this.priceList);
                this.pageParams.pageSize = data.meta.itemsPerPage;
                this.pageParams.pageIndex = data.meta.currentPage - 1;
                this.pageParams.length = data.meta.totalItems;

                this.setDynamicColumns();

            }, (err) => {
                this.notify.showError(err.error.message);
            }, () => {
                this.showLoader = false;
            });
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getList();
    }

    public sortData(sort: Sort): void {
        const data = this.dataSource;
        if (!sort.active || sort.direction === '') {
            this.dataSource = data;
            return;
        }
        this.currentSort = sort;
        this.getList();
    }

    public onFilterUpdate(value: IPriceListFilter): void {
        this.searchValue = value.search;
        this.filters = value;
        this.getList();
    }

    public update(data: IShipmentPriceAction): void {
        this.showLoader = true;

        this.shipmentPriceSvc.update<ISubCarrierPriceError>('sub-carrier-prices-error', data.element.id, this.getDataToUpdate(data))
            .subscribe((p) => {
                if (!p) {
                    this.notify.showError(`Something went wrong`);
                }
                this.notify.showSuccessMessage(`Updated successfully`);
                data.onSuccess();
                this.removeData(data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
            }, () => {
                this.showLoader = false;
            });
    }

    private getDataToUpdate(data): any {
        const dataToUpdate = {...data.form};
        const prices = [];
        Object.entries(data.form.prices)
            .forEach(([key, value]) => {
                if (value && !isNaN(+value)) {
                    prices.push({cost: value, name: key});
                }
            });
        dataToUpdate.prices = prices;
        return dataToUpdate;
    }

    public delete(element: { id: number; index: number }): void {
        this.showLoader = true;
        this.shipmentPriceSvc.delete('sub-carrier-prices-error', element.id)
            .subscribe((p) => {
                if (!p) {
                    this.notify.showError(`Something went wrong`);
                }
                this.notify.showSuccessMessage(`Deleted successfully`);
                this.removeData(element.index);
            }, (err) => {
                this.notify.showError(err.error.message);
            }, (): void => {
                this.showLoader = false;
            });
    }

    public removeData(index: number): void {
        this.priceList.splice(index, 1);
        this.dataSource.data = this.priceList;
    }

    private setDynamicColumns(): void {
        if (this.dynamicColumns) {
            return;
        }
        const subCarrierPriceError = this.priceList.reduce((accumulator, currentArray) => {
            return accumulator.concat(currentArray.prices);
        }, []);
        const allColumns = subCarrierPriceError?.map((p) => p.name);

        // Convert to Set to remove duplicates, then back to array
        this.dynamicColumns = [...new Set(allColumns)];

        if (this.dynamicColumns?.length > 0) {
            // Insert new columns before errorReason (index = 12)
            this.displayedColumns.splice(12, 0, ...this.dynamicColumns);
        }
    }
}
